import React, { useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  DatePicker,
  Modal,
  Tooltip,
  TreeSelect,
  notification,
} from 'antd'
import moment from 'moment'
import styled from 'styled-components'
import { saveAs } from 'file-saver'
import { expressAPI } from 'src/utils/interceptor'
import ReportGroupSelect from './ReportGroupSelect'

const dateFormat = 'DD/MM/YYYY'
const monthFormat = 'MMMM YYYY'

export const LONG_RANGE_DAYS = 90
export const HEAVY_VENUE_COUNT = 20
export const REPORT_VENUE_CONFIRM_COUNT = 40
export const EMPTY_ACCRUALS_NOTE = 'No incomplete orders in this period'

export function getDefaultMonthMoment(now = moment()) {
  return now.clone().subtract(1, 'month').startOf('month')
}

export function monthToDateRange(monthMoment) {
  if (!monthMoment || !monthMoment.isValid?.()) return [null, null]
  return [
    monthMoment.clone().startOf('month'),
    monthMoment.clone().endOf('month'),
  ]
}

export function getInclusiveRangeDays(start, end) {
  if (!start || !end || !start.isValid?.() || !end.isValid?.()) return 0
  return (
    end.clone().startOf('day').diff(start.clone().startOf('day'), 'days') + 1
  )
}

export function shouldWarnLongRange(dayCount) {
  return dayCount > LONG_RANGE_DAYS
}

export function shouldWarnHeavyReport(venueCount) {
  return Number(venueCount) >= HEAVY_VENUE_COUNT
}

export function shouldConfirmAccrualsVenueCount(count) {
  return Number(count) > REPORT_VENUE_CONFIRM_COUNT
}

export function accrualsLargeGroupQuestion(count) {
  return `This accruals report has ${count} venues. Generate it now?`
}

export function getFilenameFromContentDisposition(headerValue) {
  if (!headerValue) return ''
  const utf8Match = headerValue.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]).replace(/["']/g, '')
  }

  const fallbackMatch = headerValue.match(/filename="?([^"]+)"?/i)
  return fallbackMatch?.[1] || ''
}

export function buildAccrualsFallbackFilename({
  start,
  end,
  venueCount = 0,
  mode,
  generatedAt = moment(),
} = {}) {
  const startDate = moment(start, 'YYYY-MM-DD', true)
  const endDate = moment(end, 'YYYY-MM-DD', true)
  const isCalendarMonth =
    mode === 'month' ||
    (startDate.isValid() &&
      endDate.isValid() &&
      startDate.isSame(startDate.clone().startOf('month'), 'day') &&
      endDate.isSame(endDate.clone().endOf('month'), 'day') &&
      startDate.isSame(endDate, 'month') &&
      startDate.isSame(endDate, 'year'))

  let periodPart = 'period'
  if (startDate.isValid() && endDate.isValid()) {
    periodPart = isCalendarMonth
      ? startDate.format('YYYY-MM')
      : `${startDate.format('YYYY-MM-DD')}_to_${endDate.format('YYYY-MM-DD')}`
  }

  const stamp = moment(generatedAt).utc().format('YYYYMMDD[T]HHmm')
  return `accruals_${periodPart}_${Number(venueCount) || 0}-venues_${stamp}.csv`
}

export function isEmptyAccrualsCsv(csvBody) {
  return String(csvBody || '').includes(EMPTY_ACCRUALS_NOTE)
}

/**
 * Accruals report panel — multi-venue CSV download for incomplete POs.
 */
const AccrualsReport = ({ venues = [] }) => {
  const [selectedVenueIds, setSelectedVenueIds] = useState([])
  const [dateMode, setDateMode] = useState('month') // 'month' | 'custom'
  const [selectedMonth, setSelectedMonth] = useState(() =>
    getDefaultMonthMoment(),
  )
  const [customRange, setCustomRange] = useState(() =>
    monthToDateRange(getDefaultMonthMoment()),
  )
  const [showProvisionalOptions, setShowProvisionalOptions] = useState(false)
  const [includeAwaitingSupplier, setIncludeAwaitingSupplier] = useState(false)
  const [includeCreditWorkflow, setIncludeCreditWorkflow] = useState(false)
  const [includeSentToXero, setIncludeSentToXero] = useState(false)
  const [loadingReport, setLoadingReport] = useState(false)

  const venueOptions = useMemo(
    () =>
      venues.map(item => ({
        title: item.title,
        value: item._id,
      })),
    [venues],
  )
  const allVenueIds = useMemo(
    () => venueOptions.map(({ value }) => value),
    [venueOptions],
  )

  const effectiveRange = useMemo(() => {
    if (dateMode === 'month') {
      return monthToDateRange(selectedMonth)
    }
    return customRange || [null, null]
  }, [dateMode, selectedMonth, customRange])

  const rangeDayCount = getInclusiveRangeDays(
    effectiveRange?.[0],
    effectiveRange?.[1],
  )
  const showLongRangeWarning = shouldWarnLongRange(rangeDayCount)
  const showHeavyReportWarning = shouldWarnHeavyReport(
    selectedVenueIds.length,
    rangeDayCount,
  )

  const canRunReport =
    selectedVenueIds.length > 0 &&
    Boolean(effectiveRange?.[0] && effectiveRange?.[1])

  const runReport = async () => {
    if (!canRunReport || loadingReport) return

    const start = effectiveRange[0].format('YYYY-MM-DD')
    const end = effectiveRange[1].format('YYYY-MM-DD')

    setLoadingReport(true)
    try {
      const response = await expressAPI.post(
        'venues/accruals-report',
        {
          venueIds: selectedVenueIds,
          start,
          end,
          mode: dateMode,
          includeAwaitingSupplier,
          includeCreditWorkflow,
          includeSentToXero,
        },
        { responseType: 'text' },
      )

      const csvBody = response?.data ?? ''
      const filename =
        getFilenameFromContentDisposition(
          response.headers?.['content-disposition'],
        ) ||
        buildAccrualsFallbackFilename({
          start,
          end,
          venueCount: selectedVenueIds.length,
          mode: dateMode,
        })

      const blob = new Blob([csvBody], {
        type: 'text/csv;charset=utf-8;',
      })
      saveAs(blob, filename)

      if (isEmptyAccrualsCsv(csvBody)) {
        notification.success({
          message: 'Report generated',
          description: EMPTY_ACCRUALS_NOTE,
        })
      } else {
        notification.success({
          message: 'Report generated',
          description:
            'Accruals CSV downloaded. Large multi-venue runs can take a moment — thanks for waiting.',
        })
      }
    } catch (error) {
      console.error('Failed to download Accruals report:', error)
      notification.error({
        message: 'Report failed',
        description:
          'We could not generate the Accruals report. Please try again.',
      })
    } finally {
      setLoadingReport(false)
    }
  }

  const askThenRun = () => {
    if (!shouldConfirmAccrualsVenueCount(selectedVenueIds.length)) {
      runReport()
      return
    }
    Modal.confirm({
      title: 'Large accruals report',
      content: accrualsLargeGroupQuestion(selectedVenueIds.length),
      okText: 'Generate report',
      cancelText: 'Cancel',
      onOk: () => runReport(),
    })
  }

  return (
    <StyledBlock>
      <HelperText data-testid='report-helper'>
        Downloads incomplete purchase orders for month-end accruals. Uses{' '}
        <strong>ordered date</strong>, same as Goods receipting.{' '}
        <strong>Accrual total</strong> excludes invoiced or Xero-complete
        orders. Unreconciled invoices appear under{' '}
        <strong>Invoice backlog</strong> — do not accrue those twice.
      </HelperText>

      <ReportGroupSelect venues={venues} onPick={setSelectedVenueIds} />
      <TreeSelect
        treeNodeFilterProp='title'
        allowClear
        placeholder='Select Venues ...'
        treeCheckable
        showCheckedStrategy={TreeSelect.SHOW_CHILD}
        dropdownStyle={{ maxHeight: '300px' }}
        style={{ marginBottom: '15px', width: '355px' }}
        onChange={ids => setSelectedVenueIds(ids)}
        value={selectedVenueIds}
        treeData={[
          {
            title:
              selectedVenueIds.length > 0 ? (
                <span
                  onClick={() => setSelectedVenueIds([])}
                  style={{
                    display: 'inline-block',
                    color: '#b5d922',
                    cursor: 'pointer',
                  }}
                >
                  Unselect all
                </span>
              ) : (
                <span
                  onClick={() => setSelectedVenueIds(allVenueIds)}
                  style={{
                    display: 'inline-block',
                    color: '#b5d922',
                    cursor: 'pointer',
                  }}
                >
                  Select all
                </span>
              ),
            value: 'select-all-venues',
            disableCheckbox: true,
            disabled: true,
          },
          ...venueOptions,
        ]}
      />

      <ModeRow>
        <ModeButton
          type='button'
          $active={dateMode === 'month'}
          onClick={() => setDateMode('month')}
          data-testid='date-mode-month'
        >
          Month
        </ModeButton>
        <ModeButton
          type='button'
          $active={dateMode === 'custom'}
          onClick={() => setDateMode('custom')}
          data-testid='date-mode-custom'
        >
          Custom range
        </ModeButton>
      </ModeRow>

      {dateMode === 'month' ? (
        <StyledMonthPicker
          data-testid='month-picker'
          value={selectedMonth}
          onChange={value => {
            setSelectedMonth(value)
            if (value) {
              setCustomRange(monthToDateRange(value))
            }
          }}
          format={monthFormat}
          allowClear={false}
          style={{ width: '355px', marginBottom: '15px' }}
          placeholder='Select month'
        />
      ) : (
        <StyledRangePicker
          data-testid='date-range-picker'
          value={customRange}
          onChange={value => setCustomRange(value)}
          format={dateFormat}
          style={{ width: '355px', marginBottom: '15px' }}
        />
      )}

      {showLongRangeWarning && (
        <WarningText data-testid='warning-long-range'>
          This range is longer than 90 days. Generation may take a while —
          please keep this window open until the download finishes. You can
          still run the report.
        </WarningText>
      )}

      {showHeavyReportWarning && (
        <WarningText data-testid='warning-heavy-report'>
          You selected {selectedVenueIds.length} venues. This can take a minute
          or more, including for a normal month. This is a warning only — it
          does not block the download.
        </WarningText>
      )}

      <AdvancedToggle
        type='button'
        data-testid='toggle-provisional'
        onClick={() => setShowProvisionalOptions(prev => !prev)}
      >
        {showProvisionalOptions ? 'Hide' : 'Include'} provisional orders
      </AdvancedToggle>

      {showProvisionalOptions && (
        <AdvancedPanel data-testid='provisional-options'>
          <AdvancedHint>
            Included rows use these Reason values and count toward{' '}
            <strong>Provisional total</strong>, not Accrual total.
          </AdvancedHint>
          <Checkbox
            checked={includeAwaitingSupplier}
            onChange={e => setIncludeAwaitingSupplier(e.target.checked)}
            data-testid='flag-awaiting-supplier'
          >
            Awaiting supplier confirmation
          </Checkbox>
          <Checkbox
            checked={includeCreditWorkflow}
            onChange={e => setIncludeCreditWorkflow(e.target.checked)}
            data-testid='flag-credit-workflow'
          >
            In credit workflow
          </Checkbox>
          <Checkbox
            checked={includeSentToXero}
            onChange={e => setIncludeSentToXero(e.target.checked)}
            data-testid='flag-sent-to-xero'
          >
            Sent to Xero – review in accounting
          </Checkbox>
        </AdvancedPanel>
      )}

      {!canRunReport ? (
        <Tooltip title='Select venues and a period to run this report.'>
          {/* Span wrapper: disabled buttons don't fire mouse events for Tooltip */}
          <span
            style={{ display: 'inline-block', width: '355px', marginTop: 20 }}
          >
            <Button style={{ width: '100%' }} disabled data-testid='run-report'>
              Run Report
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Button
          loading={loadingReport}
          style={{ width: '355px', marginTop: 20 }}
          className='ant-btn ant-btn-primary'
          onClick={askThenRun}
          disabled={loadingReport}
          data-testid='run-report'
        >
          {loadingReport ? 'Generating… This may take a minute' : 'Run Report'}
        </Button>
      )}
    </StyledBlock>
  )
}

const StyledBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const HelperText = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
`

const WarningText = styled.p`
  font-size: 13px;
  color: #8a6d3b;
  background: #fcf8e3;
  border: 1px solid #faebcc;
  border-radius: 4px;
  padding: 8px 10px;
  margin: 0 0 8px;
  max-width: 355px;
`

const ModeRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
`

const ModeButton = styled.button`
  border: 1px solid ${props => (props.$active ? '#b5d922' : '#d9d9d9')};
  background: ${props => (props.$active ? '#f6ffed' : '#fff')};
  color: ${props => (props.$active ? '#333' : '#666')};
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;
`

const AdvancedToggle = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  color: #1890ff;
  cursor: pointer;
  font-size: 13px;
`

const AdvancedPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 355px;
`

const AdvancedHint = styled.p`
  font-size: 12px;
  color: #666;
  margin: 0 0 4px;
`

const StyledRangePicker = styled(DatePicker.RangePicker)`
  .ant-calendar-picker-input.ant-input {
    height: 40px;
    padding: 6px 6px;
    font-size: 16px;
  }
`

const StyledMonthPicker = styled(DatePicker.MonthPicker)`
  .ant-calendar-picker-input.ant-input {
    height: 40px;
    padding: 6px 6px;
    font-size: 16px;
  }
`

export default AccrualsReport
